// src/components/AdministracionFinancieras.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Spinner } from "react-bootstrap";

import {
  getFinancierasAdmin,
  cambiarEstadoFinanciera,
} from "../helpers/rutaFinancieras";

import ModalAdministrarFinanciera from "./ModalAdministrarFinanciera";

import "../css/administracionFinancieras.css";

const clean = (v) =>
  String(v ?? "").trim();

const getId = (f) =>
  f?._id || f?.id || "";

const inicialesFinanciera = (nombre) => {
  const palabras = clean(nombre)
    .split(/\s+/)
    .filter(Boolean);

  if (!palabras.length) {
    return "F";
  }

  return palabras
    .slice(0, 2)
    .map((p) =>
      p.charAt(0).toUpperCase()
    )
    .join("");
};

const textoTipoCalculo = (tipo) => {
  switch (
    clean(tipo).toUpperCase()
  ) {
    case "MONTO_DIRECTO":
      return "Monto directo";

    case "POR_CUOTA":
      return "Por cuota";

    case "FONDEO_INVERSO":
      return "Fondeo inverso";

    case "GROSS_UP":
    default:
      return "Gross-up";
  }
};

const AdministracionFinancieras = () => {
  const [
    financieras,
    setFinancieras,
  ] = useState([]);

  const [
    resumen,
    setResumen,
  ] = useState({
    activas: 0,
    inactivas: 0,
    conFee: 0,
    conCuotas: 0,
  });

  const [
    total,
    setTotal,
  ] = useState(0);

  const [
    page,
    setPage,
  ] = useState(1);

  const limit = 100;

  const [
    q,
    setQ,
  ] = useState("");

  const [
    estado,
    setEstado,
  ] = useState("TODAS");

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const [
    showModal,
    setShowModal,
  ] = useState(false);

  const [
    seleccionada,
    setSeleccionada,
  ] = useState(null);

  const [
    cambiandoEstadoId,
    setCambiandoEstadoId,
  ] = useState("");

  /* ======================================================
     CARGA
  ====================================================== */

  const cargarFinancieras =
    useCallback(async () => {
      try {
        setLoading(true);
        setError("");

        const resp =
          await getFinancierasAdmin({
            page,
            limit,
            q,
            estado,
          });

        setFinancieras(
          Array.isArray(
            resp?.financieras
          )
            ? resp.financieras
            : []
        );

        setResumen({
          activas:
            Number(
              resp?.resumen
                ?.activas || 0
            ),

          inactivas:
            Number(
              resp?.resumen
                ?.inactivas || 0
            ),

          conFee:
            Number(
              resp?.resumen
                ?.conFee || 0
            ),

          conCuotas:
            Number(
              resp?.resumen
                ?.conCuotas || 0
            ),
        });

        setTotal(
          Number(
            resp?.total || 0
          )
        );
      } catch (err) {
        console.error(
          "❌ Error cargando financieras:",
          err
        );

        setError(
          err?.response?.data
            ?.error ||
            err?.response?.data
              ?.message ||
            err?.message ||
            "No se pudieron cargar las financieras."
        );
      } finally {
        setLoading(false);
      }
    }, [
      page,
      q,
      estado,
    ]);

  /*
    Debounce del buscador.
  */
  useEffect(() => {
    const timer =
      setTimeout(() => {
        cargarFinancieras();
      }, 300);

    return () =>
      clearTimeout(timer);
  }, [cargarFinancieras]);

  /*
    Si cambia filtro o búsqueda,
    volvemos a página 1.
  */
  useEffect(() => {
    setPage(1);
  }, [q, estado]);

  /* ======================================================
     MODAL
  ====================================================== */

  const nuevaFinanciera = () => {
    setSeleccionada(null);
    setShowModal(true);
  };

  const editarFinanciera = (
    financiera
  ) => {
    setSeleccionada(
      financiera
    );

    setShowModal(true);
  };

  const cerrarModal = () => {
    setShowModal(false);
    setSeleccionada(null);
  };

  const despuesDeGuardar =
    async () => {
      cerrarModal();
      await cargarFinancieras();
    };

  /* ======================================================
     ACTIVO / INACTIVO
  ====================================================== */

  const cambiarEstado =
    async (financiera) => {
      const id =
        getId(financiera);

      if (!id) {
        return;
      }

      const activoActual =
        financiera?.activo !==
        false;

      const nuevoEstado =
        !activoActual;

      if (!nuevoEstado) {
        const confirmar =
          window.confirm(
            `¿Desactivar "${clean(
              financiera.financiera
            )}"?\n\nDejará de aparecer en las nuevas operaciones.`
          );

        if (!confirmar) {
          return;
        }
      }

      try {
        setCambiandoEstadoId(
          id
        );

        await cambiarEstadoFinanciera(
          id,
          nuevoEstado
        );

        await cargarFinancieras();
      } catch (err) {
        console.error(
          "❌ Error cambiando estado:",
          err
        );

        alert(
          err?.response?.data
            ?.error ||
            err?.response?.data
              ?.message ||
            "No se pudo cambiar el estado de la financiera."
        );
      } finally {
        setCambiandoEstadoId(
          ""
        );
      }
    };

  /* ======================================================
     DATOS DERIVADOS
  ====================================================== */

  const totalPaginas =
    useMemo(() => {
      return Math.max(
        1,
        Math.ceil(
          total / limit
        )
      );
    }, [total]);

  /* ======================================================
     RENDER
  ====================================================== */

  return (
    <div className="fin-admin">
      {/* ================================================
          CABECERA
      ================================================= */}

      <div className="fin-admin-header">
        <div>
          <h2>
            Administración de
            Financieras
          </h2>

          <p>
            Configuración de
            entidades, fees,
            cuotas y disponibilidad
            operativa.
          </p>
        </div>

        <button
          type="button"
          className="fin-admin-btn-primary"
          onClick={
            nuevaFinanciera
          }
        >
          <span className="fin-admin-plus">
            +
          </span>

          Nueva financiera
        </button>
      </div>

      {/* ================================================
          KPI
      ================================================= */}

      <div className="fin-admin-kpis">
        <button
          type="button"
          className={`fin-admin-kpi ${
            estado === "ACTIVAS"
              ? "active"
              : ""
          }`}
          onClick={() =>
            setEstado(
              "ACTIVAS"
            )
          }
        >
          <span className="fin-admin-kpi-label">
            Activas
          </span>

          <strong>
            {resumen.activas}
          </strong>

          <small>
            Disponibles para
            operar
          </small>
        </button>

        <button
          type="button"
          className={`fin-admin-kpi ${
            estado ===
            "INACTIVAS"
              ? "active"
              : ""
          }`}
          onClick={() =>
            setEstado(
              "INACTIVAS"
            )
          }
        >
          <span className="fin-admin-kpi-label">
            Inactivas
          </span>

          <strong>
            {resumen.inactivas}
          </strong>

          <small>
            No visibles en ventas
          </small>
        </button>

        <button
          type="button"
          className="fin-admin-kpi"
          onClick={() =>
            setEstado("TODAS")
          }
        >
          <span className="fin-admin-kpi-label">
            Configuradas
          </span>

          <strong>
            {resumen.conFee}
          </strong>

          <small>
            Con fee o reglas
          </small>
        </button>

        <button
          type="button"
          className="fin-admin-kpi"
          onClick={() =>
            setEstado("TODAS")
          }
        >
          <span className="fin-admin-kpi-label">
            Con cuotas
          </span>

          <strong>
            {resumen.conCuotas}
          </strong>

          <small>
            Requieren plan
          </small>
        </button>
      </div>

      {/* ================================================
          FILTROS
      ================================================= */}

      <div className="fin-admin-toolbar">
        <div className="fin-admin-search">
          <span>
            🔎
          </span>

          <input
            type="text"
            placeholder="Buscar por nombre, razón social, CUIT o código..."
            value={q}
            onChange={(e) =>
              setQ(
                e.target.value
              )
            }
          />

          {q && (
            <button
              type="button"
              onClick={() =>
                setQ("")
              }
              title="Limpiar búsqueda"
            >
              ×
            </button>
          )}
        </div>

        <div className="fin-admin-filter-group">
          <button
            type="button"
            className={
              estado ===
              "TODAS"
                ? "active"
                : ""
            }
            onClick={() =>
              setEstado(
                "TODAS"
              )
            }
          >
            Todas
          </button>

          <button
            type="button"
            className={
              estado ===
              "ACTIVAS"
                ? "active"
                : ""
            }
            onClick={() =>
              setEstado(
                "ACTIVAS"
              )
            }
          >
            Activas
          </button>

          <button
            type="button"
            className={
              estado ===
              "INACTIVAS"
                ? "active"
                : ""
            }
            onClick={() =>
              setEstado(
                "INACTIVAS"
              )
            }
          >
            Inactivas
          </button>
        </div>
      </div>

      {/* ================================================
          CONTENIDO
      ================================================= */}

      <div className="fin-admin-card">
        {error && (
          <div className="fin-admin-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="fin-admin-loading">
            <Spinner
              animation="border"
              size="sm"
            />

            <span>
              Cargando
              financieras...
            </span>
          </div>
        ) : (
          <div className="fin-admin-table-wrap">
            <table className="fin-admin-table">
              <thead>
                <tr>
                  <th>
                    Logo
                  </th>

                  <th>
                    Código
                  </th>

                  <th>
                    Financiera
                  </th>

                  <th className="text-center">
                    Fee
                  </th>

                  <th className="text-center">
                    Tax
                  </th>

                  <th className="text-center">
                    Estado
                  </th>

                  <th>
                    Cuotas
                  </th>

                  <th>
                    Cálculo
                  </th>

                  <th className="text-center">
                    Acciones
                  </th>
                </tr>
              </thead>

              <tbody>
                {financieras.length ===
                0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="fin-admin-empty"
                    >
                      No se encontraron
                      financieras.
                    </td>
                  </tr>
                ) : (
                  financieras.map(
                    (f) => {
                      const id =
                        getId(f);

                      const activa =
                        f?.activo !==
                        false;

                      const reglas =
                        Array.isArray(
                          f?.reglasCuotas
                        )
                          ? f.reglasCuotas
                          : [];

                      const tieneCuotas =
                        Boolean(
                          f?.requiereCuotas
                        );

                      return (
                        <tr
                          key={
                            id ||
                            f.codfinanciera
                          }
                          className={
                            activa
                              ? ""
                              : "fin-admin-row-inactive"
                          }
                        >
                          <td>
                            <div className="fin-admin-logo">
                              {f?.logo
                                ?.url ? (
                                <img
                                  src={
                                    f
                                      .logo
                                      .url
                                  }
                                  alt={
                                    clean(
                                      f.financiera
                                    ) ||
                                    "Logo"
                                  }
                                />
                              ) : (
                                <span>
                                  {inicialesFinanciera(
                                    f.financiera
                                  )}
                                </span>
                              )}
                            </div>
                          </td>

                          <td>
                            <span className="fin-admin-code">
                              {f.codfinanciera ||
                                "-"}
                            </span>
                          </td>

                          <td>
                            <div className="fin-admin-name">
                              <strong>
                                {clean(
                                  f.financiera
                                ) ||
                                  "-"}
                              </strong>

                              {clean(
                                f.razonsocial
                              ) && (
                                <small>
                                  {clean(
                                    f.razonsocial
                                  )}
                                </small>
                              )}
                            </div>
                          </td>

                          <td className="text-center">
                            <span className="fin-admin-fee">
                              {Number(
                                f?.feePorcentaje ||
                                  0
                              ).toFixed(
                                2
                              )}
                              %
                            </span>
                          </td>

                          <td className="text-center">
                            <span
                              className={
                                f?.aplicaTax
                                  ? "fin-admin-status active"
                                  : "fin-admin-status inactive"
                              }
                            >
                              {f?.aplicaTax
                                ? "SI"
                                : "NO"}
                            </span>
                          </td>

                          <td className="text-center">
                            <div className="fin-admin-state-wrap">
                              <button
                                type="button"
                                className={`fin-admin-switch ${
                                  activa
                                    ? "on"
                                    : "off"
                                }`}
                                onClick={() =>
                                  cambiarEstado(
                                    f
                                  )
                                }
                                disabled={
                                  cambiandoEstadoId ===
                                  id
                                }
                                title={
                                  activa
                                    ? "Desactivar"
                                    : "Activar"
                                }
                              >
                                <span />
                              </button>

                              <small
                                className={
                                  activa
                                    ? "fin-admin-status active"
                                    : "fin-admin-status inactive"
                                }
                              >
                                {activa
                                  ? "Activa"
                                  : "Inactiva"}
                              </small>
                            </div>
                          </td>

                          <td>
                            {tieneCuotas ? (
                              <div className="fin-admin-cuotas">
                                {reglas.length >
                                0 ? (
                                  reglas.map(
                                    (
                                      regla,
                                      index
                                    ) => (
                                      <span
                                        key={`${regla.cuotas}-${index}`}
                                      >
                                        {
                                          regla.cuotas
                                        }
                                        x ·{" "}
                                        {Number(
                                          regla.feePorcentaje ||
                                            0
                                        ).toFixed(
                                          2
                                        )}
                                        %
                                      </span>
                                    )
                                  )
                                ) : (
                                  <span>
                                    Requiere
                                    cuotas
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="fin-admin-muted">
                                No requiere
                              </span>
                            )}
                          </td>

                          <td>
                            <span className="fin-admin-calc">
                              {textoTipoCalculo(
                                f.tipoCalculo
                              )}
                            </span>
                          </td>

                          <td className="text-center">
                            <button
                              type="button"
                              className="fin-admin-btn-edit"
                              onClick={() =>
                                editarFinanciera(
                                  f
                                )
                              }
                            >
                              ✎ Editar
                            </button>
                          </td>
                        </tr>
                      );
                    }
                  )
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* ==============================================
            FOOTER
        =============================================== */}

        <div className="fin-admin-footer">
          <span>
            {total === 0
              ? "Sin resultados"
              : `${total} financiera${
                  total === 1
                    ? ""
                    : "s"
                }`}
          </span>

          <div className="fin-admin-pagination">
            <button
              type="button"
              disabled={
                page <= 1 ||
                loading
              }
              onClick={() =>
                setPage((p) =>
                  Math.max(
                    1,
                    p - 1
                  )
                )
              }
            >
              ‹
            </button>

            <span>
              Página {page} de{" "}
              {totalPaginas}
            </span>

            <button
              type="button"
              disabled={
                page >=
                  totalPaginas ||
                loading
              }
              onClick={() =>
                setPage((p) =>
                  Math.min(
                    totalPaginas,
                    p + 1
                  )
                )
              }
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {/* ================================================
          MODAL NUEVO
      ================================================= */}

      <ModalAdministrarFinanciera
        show={showModal}
        onHide={cerrarModal}
        financiera={
          seleccionada
        }
        onSaved={
          despuesDeGuardar
        }
      />
    </div>
  );
};

export default AdministracionFinancieras;